import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Zap,
  Plus,
  ListTodo,
  Search,
  X,
} from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { cn } from '../../utils/cn';

export function QuickActionFAB() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const isApprover = user?.role === 'approver' || user?.role === 'admin';

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const actions = [
    ...(isApprover ? [{
      id: 'new-project',
      label: t('project.create'),
      icon: Plus,
      color: 'bg-green-500 hover:bg-green-600',
      onClick: () => { navigate('/projects/new'); setOpen(false); },
    }] : []),
    {
      id: 'my-tasks',
      label: t('nav.myTasks'),
      icon: ListTodo,
      color: 'bg-blue-500 hover:bg-blue-600',
      onClick: () => { navigate('/my-tasks'); setOpen(false); },
    },
    {
      id: 'search',
      label: t('fab.search'),
      icon: Search,
      color: 'bg-purple-500 hover:bg-purple-600',
      onClick: () => {
        setOpen(false);
        window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', ctrlKey: true }));
      },
    },
  ];

  return (
    <div ref={containerRef} className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-2 pointer-events-none">
      {/* Action items */}
      <div className={cn(
        'flex flex-col gap-2 transition-all duration-200',
        open ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-4 pointer-events-none'
      )}>
        {actions.map((action) => (
          <button
            key={action.id}
            onClick={action.onClick}
            className="flex items-center gap-2 pl-3 pr-4 py-2 rounded-full text-white shadow-lg text-sm font-medium transition-all hover:scale-105"
            style={{ animationDelay: `${actions.indexOf(action) * 50}ms` }}
          >
            <span className={cn('p-1.5 rounded-full', action.color)}>
              <action.icon className="w-4 h-4" />
            </span>
            <span className="bg-gray-800 dark:bg-gray-700 px-3 py-1 rounded-full whitespace-nowrap">
              {action.label}
            </span>
          </button>
        ))}
      </div>

      {/* Main FAB button */}
      <button
        onClick={() => setOpen(prev => !prev)}
        className={cn(
          'w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-110 pointer-events-auto',
          open
            ? 'bg-gray-600 dark:bg-gray-500 rotate-45'
            : 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500'
        )}
        aria-label={t('fab.quickActions')}
      >
        {open ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <Zap className="w-6 h-6 text-white" />
        )}
      </button>
    </div>
  );
}
